/**
 * File: apps/qbench-cp/reactapp/src/Order/StatusBarWizard/index.js
 *
 * Purpose: Renders the order-creation wizard's step-progress status bar, showing each step's
 *     completed/active/pending state and allowing navigation back to completed steps.
 *
 * Scope: Order creation wizard, status bar / step-progress indicator.
 *
 * Tags: customer-portal, orders
 *
 * Overview: Renders one StatusBarCircle per wizard step (create order, add samples, select
 *     tests, review order, confirmation), dispatching the matching GOTO_* action when a
 *     completed or previously-seen step is clicked. The "select tests" and "review order" steps
 *     can be hidden entirely via the ORDER_REQUEST_HIDE_TESTS portal-view option, in which case
 *     StatusBarStep renders null for them. Entity display names (order request, sample, test)
 *     come from useEntityDisplayName so labels follow LIMS customization.
 *
 * Exports: clsAction — map of step name to its GOTO_* dispatch action; StatusBarWizard (default)
 *     — the wizard status bar component.
 */

import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useOrderRequestDispatch } from 'Order/store';
import React, { useEffect, useState } from 'react';
import { EntityType } from 'shared/constants/entityTypes';
import { useEntityDisplayName, usePortalView } from 'shared/hooks/misc/fields';
import useResponsive from 'shared/hooks/misc/useResponsive';

import { StatusBarCircleStyle, StatusBarWizardStyle } from './Styles';

export const clsAction = {
  createOrder: 'GOTO_ORDER_DETAILS',
  addSamples: 'GOTO_ADD_SAMPLES',
  selectTests: 'GOTO_SELECT_TESTS',
  reviewOrder: 'GOTO_REVIEW_ORDER',
  confirm: 'GOTO_ORDER_CONFIRMATION',
};

const StatusBarCircle = (props) => {
  const status = props.status ? props.status.toLowerCase() : '';
  const dispatch = useOrderRequestDispatch();
  const [seen, setSeen] = useState(false);

  const onClick = () => {
    if (status === 'completed' || seen) {
      dispatch({ type: clsAction[props.step] });
    }
  };

  useEffect(() => {
    if (status === 'completed' || status === 'active') {
      setSeen(true);
    }
  }, [status]);
  return (
    <StatusBarCircleStyle className={status} status={status}>
      <span>
        {status === 'completed' && (
          <FontAwesomeIcon className="check-icon" icon={faCheck} />
        )}
      </span>
      <div className="content" onClick={onClick}>
        {props.children}
      </div>
    </StatusBarCircleStyle>
  );
};

const StatusBarStep = ({ disabled = false, text, stepName, status }) => {
  if (disabled) {
    return null;
  }

  return (
    <StatusBarCircle step={stepName} status={status}>
      {text}
    </StatusBarCircle>
  );
};

const StatusBarWizard = (props) => {
  const statuses = props.statuses;
  const [orderRequestSingular] = useEntityDisplayName(EntityType.ORDER_REQUEST);
  const [, samplePlural] = useEntityDisplayName(EntityType.SAMPLE);
  const [, testPlural] = useEntityDisplayName(EntityType.TEST);
  const deviceType = useResponsive();
  const orderRequestHideTestsOption = usePortalView('ORDER_REQUEST_HIDE_TESTS');
  const shouldShowTestsAndReviewOrderRequest =
    orderRequestHideTestsOption?.hidden === true;

  return (
    <StatusBarWizardStyle>
      {deviceType.isMobile || deviceType.isMobileOnly ? (
        ''
      ) : (
        <p className="title">{`New ${orderRequestSingular}`}</p>
      )}
      <ul>
        <StatusBarCircle step="createOrder" status={statuses.createOrder}>
          Create {orderRequestSingular}
        </StatusBarCircle>
        <StatusBarCircle step="addSamples" status={statuses.addSamples}>
          Add {samplePlural}
        </StatusBarCircle>

        <StatusBarStep
          disabled={!shouldShowTestsAndReviewOrderRequest}
          text={`Select ${testPlural}`}
          stepName="selectTests"
          status={statuses.selectTests}
        />

        <StatusBarStep
          disabled={!shouldShowTestsAndReviewOrderRequest}
          text={`Review ${orderRequestSingular}`}
          stepName="reviewOrder"
          status={statuses.reviewOrder}
        />

        <StatusBarCircle step="confirm" status={statuses.confirm}>
          Confirmation
        </StatusBarCircle>
      </ul>
    </StatusBarWizardStyle>
  );
};

export default StatusBarWizard;
